export type SemanticTone = "neutral" | "informational" | "success" | "warning" | "error";

const displayNames: Record<string, string> = {
  api: "API",
  openai: "OpenAI",
  elevenlabs: "ElevenLabs",
  gohighlevel: "GoHighLevel",
  n8n: "n8n",
  smtp: "Email",
  manual_entry: "Manual entry",
  closed_won: "Won",
  closed_lost: "Lost",
  appointment_scheduled: "Scheduled",
  review_required: "Needs review",
  needs_review: "Needs review",
  partially_configured: "Partially configured",
  not_enabled: "Not enabled",
  setup_required: "Setup required",
  dead_letter: "Dead letter",
};

function normalizedIdentifier(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replaceAll(/[\s-]+/gu, "_");
}

export function displayName(value: unknown, fallback = "Not available"): string {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  const normalized = normalizedIdentifier(raw);
  if (displayNames[normalized]) return displayNames[normalized];
  const readable = raw.replaceAll(/([a-z])([A-Z])/gu, "$1 $2").replaceAll(/[_-]+/gu, " ").toLowerCase();
  return readable.charAt(0).toUpperCase() + readable.slice(1);
}

export function sourceDisplayName(value: unknown, fallback = "Not available"): string {
  return displayName(value, fallback);
}

export function statusPresentation(value: unknown): { label: string; variant: SemanticTone } {
  const normalized = normalizedIdentifier(value) || "unknown";
  const label = displayName(normalized, "Unknown");
  if (["new", "draft", "unassigned", "inactive", "disabled", "unknown"].includes(normalized)) return { label, variant: "neutral" };
  if (["failed", "blocked", "disconnected", "error", "declined", "cancelled", "dead_letter", "closed_lost"].some((status) => normalized.includes(status))) return { label, variant: "error" };
  if (["overdue", "needs_review", "review_required", "partially_configured", "not_enabled", "setup_required", "setup_needed", "warning", "paused"].some((status) => normalized.includes(status))) return { label, variant: "warning" };
  if (["completed", "succeeded", "won", "closed_won", "active", "enabled", "synced", "healthy", "ready", "connected", "configured", "verified", "success", "sent", "received", "delivered"].some((status) => normalized.includes(status))) return { label, variant: "success" };
  if (["qualified", "scheduled", "processing", "pending", "queued", "confirmed", "contacting", "nurture", "rescheduled", "tentative"].some((status) => normalized.includes(status))) return { label, variant: "informational" };
  return { label, variant: "neutral" };
}
